import { createRouter, createWebHistory } from "vue-router";
import { getParentToken } from "@/services/portalService";
import staffService from "@/services/staff";

// ── Website pages (eager-loaded for fast initial render) ──────────────────
import HomePage      from "@/views/HomePage.vue";
import SasAdmission  from "@/views/SasAdmission.vue";
import Contact       from "@/views/Contact.vue";
import Testimonials  from "@/views/Testimonials.vue";
import Gallery       from "@/views/Gallery.vue";
import PrivacyPolicy from "@/views/PrivacyPolicy.vue";
import Staff         from "@/views/Staff.vue";
import News          from "@/views/News.vue";
import DynamicPage   from "@/views/DynamicPage.vue";

// ── Public Payment (lazy-loaded) ──────────────────────────────────────────
const PublicPayment         = () => import("@/views/PublicPayment.vue");
const PublicPaymentCallback = () => import("@/views/PublicPaymentCallback.vue");

// ── Parent Portal (lazy-loaded) ───────────────────────────────────────────
const PortalLogin           = () => import("@/views/portal/Login.vue");
const PortalDashboard       = () => import("@/views/portal/Dashboard.vue");
const PortalChargeDetail    = () => import("@/views/portal/ChargeDetail.vue");
const PortalPaymentCallback = () => import("@/views/portal/PaymentCallback.vue");

// ── Staff / Website Control Panel (lazy-loaded) ───────────────────────────
const StaffLogin      = () => import("@/views/website-cp/StaffLogin.vue");
const WcpDashboard    = () => import("@/views/website-cp/index.vue");
const WcpSettings     = () => import("@/views/website-cp/settings.vue");
const WcpPages        = () => import("@/views/website-cp/pages.vue");
const WcpHeroSlides   = () => import("@/views/website-cp/hero-slides.vue");
const WcpBlog         = () => import("@/views/website-cp/blog.vue");
const WcpPolicies     = () => import("@/views/website-cp/policies.vue");
const WcpTeam         = () => import("@/views/website-cp/team.vue");
const WcpTestimonials = () => import("@/views/website-cp/testimonials.vue");
const WcpMedia        = () => import("@/views/website-cp/media.vue");
const WcpFAQs         = () => import("@/views/website-cp/faqs.vue");
const WcpNavigation   = () => import("@/views/website-cp/navigation.vue");
const WcpContacts     = () => import("@/views/website-cp/contacts.vue");
const WcpPartners     = () => import("@/views/website-cp/partners.vue");
const WcpPrincipal    = () => import("@/views/website-cp/principal.vue");
const WcpPrograms     = () => import("@/views/website-cp/programs.vue");
const WcpAdmission    = () => import("@/views/website-cp/admission.vue");
const WcpFiles           = () => import("@/views/website-cp/files.vue");
const WcpPopup           = () => import("@/views/website-cp/popup.vue");
const WcpPortalSettings  = () => import("@/views/website-cp/portal-settings.vue");
const WcpPortalParents   = () => import("@/views/website-cp/portal-parents.vue");
const WcpPortalInvoices  = () => import("@/views/website-cp/portal-invoices.vue");
const WcpPortalChecks    = () => import("@/views/website-cp/portal-checks.vue");
const WcpPortalPayments  = () => import("@/views/website-cp/portal-payments.vue");

const routes = [
  // ── Public website ────────────────────────────────────────────────────
  { name: "Home",          path: "/",             component: HomePage },
  { name: "SasAdmission",  path: "/sasadmission", component: SasAdmission },
  { name: "Contact",       path: "/Contact",       component: Contact },
  { name: "Testimonials",  path: "/Testimonials",  component: Testimonials },
  { name: "Gallery",       path: "/Gallery",       component: Gallery },
  { name: "PrivacyPolicy", path: "/PrivacyPolicy", component: PrivacyPolicy },
  { name: "Staff",         path: "/staff",         component: Staff },
  { name: "News",          path: "/news",          component: News },

  // ── Public Payment (no auth) ────────────────────────────────────────
  { name: "PublicPayment",        path: "/payment",               component: PublicPayment },
  { name: "PublicPaymentCallback", path: "/payment/callback",     component: PublicPaymentCallback },

  // ── Parent Portal ─────────────────────────────────────────────────────
  {
    name:      "PortalLogin",
    path:      "/portal/login",
    component: PortalLogin,
    meta:      { portalPublic: true },
  },
  {
    name:      "PortalDashboard",
    path:      "/portal/dashboard",
    component: PortalDashboard,
    meta:      { requiresPortalAuth: true },
  },
  {
    name:      "PortalChargeDetail",
    path:      "/portal/charges/:id",
    component: PortalChargeDetail,
    props:     true,
    meta:      { requiresPortalAuth: true },
  },
  {
    name:      "PortalPaymentCallback",
    path:      "/portal/payment/callback",
    component: PortalPaymentCallback,
    meta:      { requiresPortalAuth: true },
  },
  {
    path:     "/portal",
    redirect: () =>
      getParentToken()
        ? { name: "PortalDashboard" }
        : { name: "PortalLogin" },
  },

  // ── Website Control Panel (Staff only) ────────────────────────────────
  {
    name:      "StaffLogin",
    path:      "/website-cp/login",
    component: StaffLogin,
    meta:      { staffPublic: true },
  },
  { name: "WcpDashboard",    path: "/website-cp",              component: WcpDashboard,    meta: { requiresStaffAuth: true } },
  { name: "WcpSettings",     path: "/website-cp/settings",     component: WcpSettings,     meta: { requiresStaffAuth: true } },
  { name: "WcpPages",        path: "/website-cp/pages",        component: WcpPages,        meta: { requiresStaffAuth: true } },
  { name: "WcpHeroSlides",   path: "/website-cp/hero-slides",  component: WcpHeroSlides,   meta: { requiresStaffAuth: true } },
  { name: "WcpBlog",         path: "/website-cp/blog",         component: WcpBlog,         meta: { requiresStaffAuth: true } },
  { name: "WcpPolicies",     path: "/website-cp/policies",     component: WcpPolicies,     meta: { requiresStaffAuth: true } },
  { name: "WcpTeam",         path: "/website-cp/team",         component: WcpTeam,         meta: { requiresStaffAuth: true } },
  { name: "WcpTestimonials", path: "/website-cp/testimonials", component: WcpTestimonials, meta: { requiresStaffAuth: true } },
  { name: "WcpMedia",        path: "/website-cp/media",        component: WcpMedia,        meta: { requiresStaffAuth: true } },
  { name: "WcpFAQs",         path: "/website-cp/faqs",         component: WcpFAQs,         meta: { requiresStaffAuth: true } },
  { name: "WcpNavigation",   path: "/website-cp/navigation",   component: WcpNavigation,   meta: { requiresStaffAuth: true } },
  { name: "WcpContacts",     path: "/website-cp/contacts",     component: WcpContacts,     meta: { requiresStaffAuth: true } },
  { name: "WcpPartners",     path: "/website-cp/partners",     component: WcpPartners,     meta: { requiresStaffAuth: true } },
  { name: "WcpPrincipal",    path: "/website-cp/principal",    component: WcpPrincipal,    meta: { requiresStaffAuth: true } },
  { name: "WcpPrograms",     path: "/website-cp/programs",     component: WcpPrograms,     meta: { requiresStaffAuth: true } },
  { name: "WcpAdmission",    path: "/website-cp/admission",    component: WcpAdmission,    meta: { requiresStaffAuth: true } },
  { name: "WcpFiles",           path: "/website-cp/files",           component: WcpFiles,          meta: { requiresStaffAuth: true } },
  { name: "WcpPopup",           path: "/website-cp/popup",           component: WcpPopup,          meta: { requiresStaffAuth: true } },
  { name: "WcpPortalSettings", path: "/website-cp/portal-settings", component: WcpPortalSettings, meta: { requiresStaffAuth: true } },
  { name: "WcpPortalParents",  path: "/website-cp/portal-parents",  component: WcpPortalParents,  meta: { requiresStaffAuth: true } },
  { name: "WcpPortalInvoices", path: "/website-cp/portal-invoices", component: WcpPortalInvoices, meta: { requiresStaffAuth: true } },
  { name: "WcpPortalChecks",   path: "/website-cp/portal-checks",   component: WcpPortalChecks,   meta: { requiresStaffAuth: true } },
  { name: "WcpPortalPayments", path: "/website-cp/portal-payments", component: WcpPortalPayments, meta: { requiresStaffAuth: true } },

  // ── Dynamic CMS pages (catch-all — must be last!) ─────────────────────
  { name: "DynamicPage", path: "/:slug", component: DynamicPage, props: true },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition;
    return { top: 0, behavior: "smooth" };
  },
});

// ── Navigation Guards ─────────────────────────────────────────────────────

router.beforeEach((to, from, next) => {
  // Parent portal guard
  if (to.meta.requiresPortalAuth && !getParentToken()) {
    return next({ name: "PortalLogin", query: { redirect: to.fullPath } });
  }

  // Staff guard — redirect to staff login if unauthenticated
  if (to.meta.requiresStaffAuth && !staffService.isLoggedIn()) {
    return next({ name: "StaffLogin", query: { redirect: to.fullPath } });
  }

  // Already logged-in staff visiting the login page → go to dashboard
  if (to.name === "StaffLogin" && staffService.isLoggedIn()) {
    return next(to.query.redirect || "/website-cp");
  }

  next();
});

export default router;
